import { API_URL } from "../config";
import type { Incident, StatusSummary } from "./types";

/**
 * Reads of the public status API.
 *
 * Deliberately does NOT reuse `api/client.ts`: that module wraps the
 * conversation endpoints and throws `ApiError` shaped for the chat flow. The
 * status page has a different failure contract — a 503 means "we have no status
 * data right now", which is a state to render, not an error to retry.
 */

export class StatusUnavailable extends Error {
  constructor(readonly status: number | null) {
    super("Status data is unavailable");
    this.name = "StatusUnavailable";
  }
}

async function getJson<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { headers: { Accept: "application/json" } });
  } catch {
    // Offline, DNS, CORS, abort — no HTTP status to report.
    throw new StatusUnavailable(null);
  }
  if (res.status === 404) throw new StatusUnavailable(404);
  if (!res.ok) throw new StatusUnavailable(res.status);
  return (await res.json()) as T;
}

export const fetchStatus = () => getJson<StatusSummary>("/status");

export const fetchIncident = (reference: string) =>
  getJson<Incident>(`/status/incidents/${encodeURIComponent(reference)}`);

export const fetchIncidents = () =>
  getJson<{ incidents: Incident[] }>("/status/incidents?limit=50");
